// Room-history store — the shared, session-scoped cache behind the desktop
// RoomsList sidebar and the mobile SwitchRoomSheet.
//
// WHY a module-level store (not a hook-local fetch):
//   - Both surfaces want the SAME data; a module singleton fetches ONCE and
//     both subscribe to it (no double query, no prop-drilling across the two
//     disjoint subtrees).
//   - The user's interaction history is derived from the backend (listMyRooms)
//     but we only want to pay for that query ONCE per page load. The result is
//     mirrored to sessionStorage so it survives in-tab remounts / route
//     changes, and a fresh tab (new session) refetches.
//   - Sending a message must surface the room immediately, before any refetch.
//     recordRoomActivity() mutates the live cache + notifies subscribers.
//
// Shape held in cache: RoomSummary[] = [{ code, lastActive }] (see
// src/lib/data/contract.js). GLOB is NOT filtered here — the hook does that so
// callers that want the raw set (e.g. tests) still see it.
import { listMyRooms } from '../../lib/data';

const SESSION_KEY_PREFIX = 'roomHistory:';

// userId -> RoomSummary[] (the authoritative in-memory snapshot).
const cache = new Map();
// userId -> Promise (in-flight load, so concurrent ensureLoaded calls dedupe).
const inflight = new Map();
// userId -> true once a load has resolved (so an empty result still counts as
// "loaded" and doesn't refetch on every mount).
const loaded = new Map();

const listeners = new Set();

function sessionKey(userId) {
  return `${SESSION_KEY_PREFIX}${userId}`;
}

function readSession(userId) {
  try {
    const raw = sessionStorage.getItem(sessionKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed
      .filter((r) => r && typeof r.code === 'string' && r.code)
      .map((r) => ({
        code: r.code,
        lastActive:
          typeof r.lastActive === 'number' && Number.isFinite(r.lastActive)
            ? r.lastActive
            : null,
      }));
  } catch {
    return null;
  }
}

function writeSession(userId, rooms) {
  try {
    sessionStorage.setItem(sessionKey(userId), JSON.stringify(rooms));
  } catch {
    /* quota / disabled storage — the in-memory cache still works */
  }
}

function notify() {
  listeners.forEach((fn) => fn());
}

// Subscribe to cache changes (for useSyncExternalStore). Returns an unsubscribe.
export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Stable snapshot for a user. Hydrates from sessionStorage on first read so a
// remount inside the same tab paints instantly without waiting for the network.
export function getSnapshot(userId) {
  if (!userId) return EMPTY;
  if (cache.has(userId)) return cache.get(userId);
  const fromSession = readSession(userId);
  if (fromSession) {
    cache.set(userId, fromSession);
    return fromSession;
  }
  return EMPTY;
}

// A single frozen empty array — returning a fresh [] each call would make
// useSyncExternalStore think the snapshot changed every render (infinite loop).
const EMPTY = Object.freeze([]);

// Fetch the user's room history once and cache it. Idempotent: a second call
// (after the first resolves, or while it's in flight) does NOT re-query.
export function ensureLoaded(userId) {
  if (!userId) return Promise.resolve(EMPTY);
  if (loaded.get(userId)) return Promise.resolve(cache.get(userId) || EMPTY);
  if (inflight.has(userId)) return inflight.get(userId);

  const p = (async () => {
    try {
      const rooms = await listMyRooms(userId);
      const clean = Array.isArray(rooms)
        ? rooms.filter((r) => r && typeof r.code === 'string' && r.code)
        : [];
      // Merge over anything recordRoomActivity added while the query was in
      // flight, so a just-sent room isn't clobbered by a stale server read.
      const pending = cache.get(userId) || [];
      const merged = mergeRooms(clean, pending);
      cache.set(userId, merged);
      loaded.set(userId, true);
      writeSession(userId, merged);
      notify();
      return merged;
    } catch {
      // Network/SDK error: don't poison `loaded` — a later mount may retry.
      // Keep whatever optimistic entries we already have.
      return cache.get(userId) || EMPTY;
    } finally {
      inflight.delete(userId);
    }
  })();

  inflight.set(userId, p);
  return p;
}

// Merge two RoomSummary lists by code, keeping the max lastActive per code.
function mergeRooms(a, b) {
  const byCode = new Map();
  [...a, ...b].forEach((r) => {
    const prev = byCode.get(r.code);
    if (prev === undefined) {
      byCode.set(r.code, r.lastActive ?? null);
      return;
    }
    const next = r.lastActive ?? null;
    if (next != null && (prev == null || next > prev)) byCode.set(r.code, next);
  });
  return [...byCode.entries()].map(([code, lastActive]) => ({ code, lastActive }));
}

// Optimistically add or bump a room — called when the user SENDS a message
// (the action that makes a room "qualify"). Surfaces the room immediately,
// before any refetch, and persists it for the rest of the tab session.
export function recordRoomActivity(userId, code, ts) {
  if (!userId || typeof code !== 'string' || !code) return;
  const at = typeof ts === 'number' && Number.isFinite(ts) ? ts : Date.now();
  const current = cache.get(userId) || readSession(userId) || [];
  const idx = current.findIndex((r) => r.code === code);
  let next;
  if (idx === -1) {
    next = [...current, { code, lastActive: at }];
  } else {
    const existing = current[idx].lastActive;
    if (existing != null && existing >= at) return; // nothing newer to record
    next = current.slice();
    next[idx] = { code, lastActive: at };
  }
  cache.set(userId, next);
  writeSession(userId, next);
  notify();
}

// Wipe room history (in-memory + sessionStorage). Called by forgetMe. With no
// userId, clears EVERYTHING — the "Leave & forget me" path doesn't always have
// the id handy and a full wipe is the safe interpretation.
export function clearRoomHistory(userId) {
  if (userId) {
    cache.delete(userId);
    inflight.delete(userId);
    loaded.delete(userId);
    try {
      sessionStorage.removeItem(sessionKey(userId));
    } catch {
      /* ignore */
    }
  } else {
    cache.clear();
    inflight.clear();
    loaded.clear();
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(SESSION_KEY_PREFIX)) {
          sessionStorage.removeItem(key);
        }
      }
    } catch {
      /* ignore */
    }
  }
  notify();
}

// Test-only escape hatch to reset module state between cases.
export const __test__ = {
  reset() {
    cache.clear();
    inflight.clear();
    loaded.clear();
    listeners.clear();
  },
  mergeRooms,
};
